import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../services/firebase.config';
import { useAuthStore } from '../../stores/authStore';
import { Eye, EyeOff } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_CONFIG } from '../../config/recaptcha';
import { ProfessionAutocomplete } from '../ui/ProfessionAutocomplete';

const registerSchema = z.object({
  name: z.string().min(2, 'Il nome deve essere di almeno 2 caratteri'),
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'La password deve essere di almeno 6 caratteri'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Le password non coincidono",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onToggleMode: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onToggleMode }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<any>(null);
  const { setUserProfile } = useAuthStore();
  
  // Stati per le posizioni di lavoro
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [professionSearch, setProfessionSearch] = useState('');
  const [showJobPreferences, setShowJobPreferences] = useState(false);
  


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    if (!captchaToken) {
      setError('Per favore completa il CAPTCHA');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        data.email, 
        data.password
      );
      
      // Update user profile with name
      await updateProfile(userCredential.user, {
        displayName: data.name
      });

      // Save user profile to Firestore
      const userProfile = {
        uid: userCredential.user.uid,
        email: data.email,
        name: data.name,
        role: 'user' as const,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        preferences: {
          emailNotifications: true,
          pushNotifications: true
        },
        // Aggiungi job preferences se sono state selezionate
        ...(selectedPositions.length > 0 && {
          jobPreferences: {
            positions: selectedPositions,
            experienceLevel: 'Mid' as const,
            remotePreference: 'Flexible' as const
          }
        })
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userProfile);
      
      // Update the auth store with the new profile
      setUserProfile(userProfile);
      
    } catch (error: any) {
      setError(error.message || 'Errore durante la registrazione');
      // Reset CAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (error && error.includes('CAPTCHA')) {
      setError(null);
    }
  };

  const handleAddPosition = (position: string) => {
    if (position.trim() && !selectedPositions.includes(position.trim())) {
      setSelectedPositions(prev => [...prev, position.trim()]);
      setProfessionSearch('');
    }
  };

  const handleRemovePosition = (position: string) => {
    setSelectedPositions(prev => prev.filter(p => p !== position));
  };

  const handleQuickPosition = (position: string) => {
    if (!selectedPositions.includes(position)) {
      setSelectedPositions(prev => [...prev, position]);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Registrati</h2>
        <p className="text-gray-600 mt-2">Crea il tuo account Traccia le tue Candidature</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Nome completo
          </label>
          <input
            type="text"
            id="name"
            {...register('name')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Mario Rossi"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            {...register('email')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="tua@email.com"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative mt-1">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              {...register('password')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Conferma password
          </label>
          <div className="relative mt-1">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              {...register('confirmPassword')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Sezione Posizioni di Lavoro (Opzionale) */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">🎯 Posizioni di Lavoro (Opzionale)</h3>
            <button
              type="button"
              onClick={() => setShowJobPreferences(!showJobPreferences)}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              {showJobPreferences ? 'Nascondi' : 'Mostra'}
            </button>
          </div>
          
          {showJobPreferences && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Seleziona le posizioni di lavoro che ti interessano. Potrai sempre modificarle in seguito dal tuo profilo.
              </p>
              
              {/* Autocompletamento Professioni */}
              <ProfessionAutocomplete
                value={professionSearch}
                onChange={setProfessionSearch}
                onAdd={handleAddPosition}
                placeholder="Cerca una professione del Ticino..."
                className="mb-3"
              />
              
              {/* Suggerimenti Rapidi */}
              <div className="mb-3">
                <p className="text-xs text-gray-600 mb-2">Suggerimenti rapidi:</p>
                <div className="flex flex-wrap gap-2">
                  {['Informatico/a', 'Infermiere/a', 'Cuoco/a', 'Impiegato/a di commercio', 'Meccanico/a di manutenzione per automobili'].map(position => (
                    <button
                      key={position}
                      type="button"
                      onClick={() => handleQuickPosition(position)}
                      disabled={selectedPositions.includes(position)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        selectedPositions.includes(position)
                          ? 'bg-green-100 text-green-800 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {position}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Posizioni Selezionate */}
              {selectedPositions.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Posizioni selezionate:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedPositions.map((position, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {position}
                        <button
                          type="button"
                          onClick={() => handleRemovePosition(position)}
                          className="text-blue-600 hover:text-blue-800 ml-1"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* CAPTCHA */}
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_CONFIG.siteKey}
            onChange={handleCaptchaChange}
            theme="light"
          />
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !captchaToken}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creazione account...' : 'Registrati'}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Hai già un account?{' '}
          <button
            onClick={onToggleMode}
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Accedi
          </button>
        </p>
      </div>
    </div>
  );
}; 